import type { FirebaseAllAvailabilitySlot } from "./allAvailabilityTypes";
import { getDatesForRestaurantDiscountFetch } from "./getDatesForRestaurantDiscountFetch";
import type { DataSnapshot } from "../dependencies/firebase";
import type { ByRestaurantId } from "../model/types";
import type { NonEmptyArray } from "../types";

/** Latest snapshot per session path — replay when date windows change without re-subscribing. */
export const latestDiscountSessionSnapshotByPath = new Map<
  string,
  DataSnapshot
>();
const latestDiscountSessionDatesByPath = new Map<string, string[]>();

export type SessionPathDefinition = {
  restaurantId: number;
  session: SessionType;
  onlyOnce: boolean;
};

export type DiscountDateAvailabilityPayload = {
  restaurantId: number;
  session: SessionType;
  date: string;
  availability: FirebaseAllAvailabilitySlot[] | null;
};

export type GetAvailabilityFromDateSnapshot = (
  snap: DataSnapshot | undefined,
  path: string,
) => FirebaseAllAvailabilitySlot[] | null;

export function sessionDefinitionToPath(
  def: Omit<SessionPathDefinition, "onlyOnce">,
): string {
  return `variableDiscountAvailability/${def.restaurantId}/${def.session.toLowerCase()}/`;
}

export function dateChildPathForSession(
  restaurantId: number,
  session: SessionType,
  date: string,
): string {
  return `variableDiscountAvailability/${restaurantId}/${session.toLowerCase()}/${date}/`;
}

export function getSessionSubscribedDefinitions(
  restaurantIds: number[],
  sessionsById: ByRestaurantId<SessionType[]>,
  onlyOnceById: ByRestaurantId<boolean>,
): SessionPathDefinition[] {
  const definitions: SessionPathDefinition[] = [];

  for (const restaurantId of restaurantIds) {
    const sessions = sessionsById[restaurantId] || [];
    const onlyOnce = onlyOnceById[restaurantId] || false;

    for (const session of sessions) {
      definitions.push({
        restaurantId,
        session,
        onlyOnce,
      });
    }
  }

  return definitions;
}

export function clearDiscountSessionSnapshotCache(path?: string): void {
  if (path === undefined) {
    latestDiscountSessionSnapshotByPath.clear();
    latestDiscountSessionDatesByPath.clear();
    return;
  }

  latestDiscountSessionSnapshotByPath.delete(path);
  latestDiscountSessionDatesByPath.delete(path);
}

export function rememberDiscountSessionDates(
  path: string,
  dates: string[],
): void {
  latestDiscountSessionDatesByPath.set(path, dates);
}

export function getRememberedDiscountSessionDates(path: string): string[] {
  return latestDiscountSessionDatesByPath.get(path) ?? [];
}

export async function emitDiscountDatesFromSessionSnapshot(
  snapshot: DataSnapshot,
  restaurantId: number,
  session: SessionType,
  dates: string[],
  getAvailabilityFromDateSnapshot: GetAvailabilityFromDateSnapshot,
  onValue: (payload: DiscountDateAvailabilityPayload) => Promise<void>,
): Promise<void> {
  await Promise.all(
    dates.map(async (date) => {
      const childSnap = snapshot.child(date);
      const availability = getAvailabilityFromDateSnapshot(
        childSnap,
        dateChildPathForSession(restaurantId, session, date),
      );
      await onValue({
        restaurantId,
        session,
        date,
        availability,
      });
    }),
  );
}

/** When date windows change but session paths are unchanged, re-merge from cache. */
export async function replayDiscountSessionSnapshots(
  sessionDefs: SessionPathDefinition[],
  missingDefinitions: SessionPathDefinition[],
  datesById: ByRestaurantId<NonEmptyArray<string>>,
  getAvailabilityFromDateSnapshot: GetAvailabilityFromDateSnapshot,
  onValue: (payload: DiscountDateAvailabilityPayload) => Promise<void>,
): Promise<void> {
  const missingPaths = new Set(missingDefinitions.map(sessionDefinitionToPath));

  await Promise.all(
    sessionDefs.map(async (def) => {
      const pathDefinition: Omit<SessionPathDefinition, "onlyOnce"> = {
        restaurantId: def.restaurantId,
        session: def.session,
      };
      const path = sessionDefinitionToPath(pathDefinition);
      if (missingPaths.has(path)) {
        return;
      }
      const snapshot = latestDiscountSessionSnapshotByPath.get(path);
      if (!snapshot) {
        return;
      }
      const dates = getDatesForRestaurantDiscountFetch(
        pathDefinition.restaurantId,
        datesById,
      );
      await emitDiscountDatesFromSessionSnapshot(
        snapshot,
        pathDefinition.restaurantId,
        pathDefinition.session,
        dates,
        getAvailabilityFromDateSnapshot,
        onValue,
      );
    }),
  );
}
